import clsx from "clsx";
import { memo, useMemo } from "react";

import classes from "./styles.module.scss";

import type { INotifCardProps } from "@components/organisms/notify/types";
import {
  INotifCardSizeEnum,
  INotifCardStyleEnum,
  INotifCardVariantsEnum,
} from "@components/organisms/notify/types";
import {
  getNotifCardIcon,
  getNotifCloseIcon,
  getNotifTextColors,
} from "@components/organisms/notify/helper";
import { Typography } from "@components/atoms/typography";
import {
  ILinkButtonSizeEnum,
  ILinkButtonVariantsEnum,
  LinkButton,
} from "@components/molecules/linkButton";
import type { IFontType } from "@/interfaces";

export const NotifCard = memo(function NotifCard({
  title,
  size = INotifCardSizeEnum.X_Small,
  variant = INotifCardVariantsEnum.Information,
  description,
  action,
  styleType = INotifCardStyleEnum.Filled,
  noClose,
  className,
}: INotifCardProps) {
  const icon = useMemo(
    () => getNotifCardIcon(variant, styleType, size),
    [variant, styleType, size],
  );
  const closeIcon = useMemo(() => getNotifCloseIcon(styleType), [styleType]);
  const textColor = useMemo(() => getNotifTextColors(styleType), [styleType]);
  const hasDescription = useMemo(() => !!description, [description]);

  const titleFontsSize = useMemo(() => {
    const titleFonts: Record<INotifCardSizeEnum, IFontType> = {
      [INotifCardSizeEnum.Large]: "label-small",
      [INotifCardSizeEnum.Small]: "paragraph-small",
      [INotifCardSizeEnum.X_Small]: "label-x-small",
    };

    return titleFonts[size];
  }, [size]);

  const actionComponent = (
    <div className={clsx(classes.notifCard__action)}>
      {action && (
        <LinkButton
          {...action}
          variant={
            styleType === INotifCardStyleEnum.Filled
              ? ILinkButtonVariantsEnum.Modifiable
              : ILinkButtonVariantsEnum.Black
          }
          size={ILinkButtonSizeEnum.Small}
        />
      )}
    </div>
  );

  return (
    <div
      data-style={styleType}
      data-size={size}
      data-variant={variant}
      className={clsx(classes.notifCard, className)}
    >
      <div className={clsx(classes.notifCard__head)}>
        <div className={clsx(classes.notifCard__icon)}>{icon}</div>
        <Typography
          className={clsx(classes.notifCard__title)}
          themeColor={textColor?.name}
          fontType={titleFontsSize}
          textWrap
        >
          {title}
        </Typography>
        {!hasDescription && actionComponent}
        {!noClose && (
          <div className={clsx(classes.notifCard__close)}>{closeIcon}</div>
        )}
      </div>
      {hasDescription && (
        <Typography
          themeColor={textColor?.name}
          fontType={
            size === INotifCardSizeEnum.X_Small
              ? "paragraph-x-small"
              : "paragraph-small"
          }
        >
          {description}
        </Typography>
      )}
      {hasDescription && (
        <div className={clsx(classes.notifCard__notifCardBottomAction)}>
          {actionComponent}
        </div>
      )}
    </div>
  );
});
NotifCard.displayName = "NotifCard";
